<script>

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import BannerSingleImageTemplate from "./templates/BannerSingleImageTemplate.vue";
import BannerClickWrapper from './BannerClickWrapper.vue'
import SlideCarousel from '@utilities/Carousel/SlideCarousel.vue'

export default {
    name: "Banner",
    inject: ['System'],
    components: {
        Loading,
        BannerSingleImageTemplate,
        BannerClickWrapper,
        SlideCarousel,
    },
    props: {
        leadBanner: {
            required: false,
            default: false,
        },
        leftHandBanners: {
            type: Array,
            default: () => [],
        },
        rightHandBanner: {
            required: false,
            default: false,
        },
        /**
         * Category / core page carousel slides (max 5 from the service).
         */
        banners: {
            type: Array,
            default: () => [],
        },
        page: {
            required: true,
        },
        currentCategoryId: {
            type: Number,
            default: null,
        },
        /**
         * Page/surface GTM creative_slot for category/core carousels
         * (e.g. category_banner, basket, favourites). Homepage left/right set in template.
         */
        gtmCreativeSlot: {
            type: String,
            default: null,
        },
    },
    computed: {
        maxCarouselSlides() {
            return 5
        },
        /**
         * Homepage mobile carousel: right-only creatives keep homepage_right; otherwise left.
         */
        homepageMobileGtmCreativeSlot() {
            return (carouselBanner) => {
                if (
                    carouselBanner.right_hand_banner
                    && ! carouselBanner.left_hand_banner
                ) {
                    return 'homepage_right'
                }

                return 'homepage_left'
            }
        },
        resolvedCategoryGtmCreativeSlot() {
            return this.gtmCreativeSlot || 'category_banner'
        },
        resolvedLeftHandBanners() {
            if (this.leftHandBanners.length) {
                return this.leftHandBanners.slice(0, this.maxCarouselSlides)
            }

            // Lead banner is also used for strip/notification and may be the
            // right-hand winner — never mirror it into the left slot.
            if (this.rightHandBanner) {
                return []
            }

            // Preview callers that only pass `leadBanner`.
            if (this.leadBanner) {
                return [this.leadBanner]
            }

            return []
        },
        resolvedCategoryBanners() {
            if (this.banners.length) {
                return this.banners
                    .filter((categoryBanner) => Boolean(categoryBanner.category_banner_mobile))
                    .slice(0, this.maxCarouselSlides)
            }

            if (this.leadBanner?.category_banner_mobile) {
                return [this.leadBanner]
            }

            return []
        },
        hasRightHandBanner() {
            return Boolean(this.rightHandBanner)
        },
        /**
         * Left slot uses the static default creative when no left-hand banners are eligible.
         */
        showDefaultLeftSlot() {
            return (this.page === 'dashboard' || this.page === 'preview')
                && this.resolvedLeftHandBanners.length === 0
        },
        /**
         * Right slot uses the static default creative when no live right-hand banner is eligible.
         */
        showDefaultRightSlot() {
            return (this.page === 'dashboard' || this.page === 'preview')
                && ! this.hasRightHandBanner
        },
        showRightHandSlot() {
            return this.hasRightHandBanner || this.showDefaultRightSlot
        },
        mobileCarouselBanners() {
            const mobileBanners = []

            if (this.resolvedLeftHandBanners.length) {
                mobileBanners.push(...this.resolvedLeftHandBanners)
            }

            if (this.hasRightHandBanner && mobileBanners.length < this.maxCarouselSlides) {
                const rightHandBannerId = this.rightHandBanner.banner_id
                const alreadyInLeft = mobileBanners.some(
                    (leftHandBanner) => leftHandBanner.banner_id === rightHandBannerId
                )

                if (! alreadyInLeft) {
                    mobileBanners.push(this.rightHandBanner)
                }
            }

            return mobileBanners.slice(0, this.maxCarouselSlides)
        },
        showHomepageLayout() {
            return this.page === 'dashboard' || this.page === 'preview'
        },
        showLeftCarouselArrows() {
            return this.resolvedLeftHandBanners.length > 1
        },
        showMobileCarouselArrows() {
            return this.mobileCarouselBanners.length > 1
        },
        showCategoryCarouselArrows() {
            return this.resolvedCategoryBanners.length > 1
        },
        defaultLeftBannerSrc() {
            return '/img/homepage/left-default-banner.png'
        },
        defaultLeftBannerSrcset() {
            return '/img/homepage/left-default-banner.png 1x, /img/homepage/left-default-banner-2x.png 2x'
        },
        defaultRightBannerSystemSuffix() {
            const systemIdentifier = this.System?.system_identifier

            if (systemIdentifier === 'IE') {
                return 'ie'
            }

            if (systemIdentifier === 'GB') {
                return 'gb'
            }

            return null
        },
        defaultRightBannerSrc() {
            if (this.defaultRightBannerSystemSuffix) {
                return `/img/homepage/right-default-banner-${this.defaultRightBannerSystemSuffix}.png`
            }

            return '/img/homepage/right-default-banner.png'
        },
        defaultRightBannerSrcset() {
            if (this.defaultRightBannerSystemSuffix) {
                const defaultRightBannerPath = `/img/homepage/right-default-banner-${this.defaultRightBannerSystemSuffix}`

                return `${defaultRightBannerPath}.png 1x, ${defaultRightBannerPath}-2x.png 2x`
            }

            return '/img/homepage/right-default-banner.png 1x, /img/homepage/right-default-banner-2x.png 2x'
        },
    },
}
</script>

<template>
    <section>
        <div v-if="page === 'dashboard' || page === 'preview'" class="dashboard-banner">
            <!-- Mobile: live left + right banners merged into one carousel (no static defaults) -->
            <div
                v-if="showHomepageLayout && mobileCarouselBanners.length"
                class="sm:hidden"
            >
                <SlideCarousel
                    :items="mobileCarouselBanners"
                    item-key="banner_id"
                    :slides-per-view="1"
                    :slides-per-view-md="1"
                    gap="0.75rem"
                    :show-arrows="showMobileCarouselArrows"
                    :show-dots="showMobileCarouselArrows"
                    arrow-placement="overlay"
                    :autoplay-delay="showMobileCarouselArrows ? 5000 : null"
                    :options="{ loop: showMobileCarouselArrows }"
                >
                    <template #default="{ item: carouselBanner }">
                        <BannerSingleImageTemplate
                            :banner="carouselBanner"
                            :current-category-id="currentCategoryId"
                            :skip-product-modal="page === 'dashboard'"
                            :gtm-creative-slot="homepageMobileGtmCreativeSlot(carouselBanner)"
                            :image-slot="carouselBanner.right_hand_banner && ! carouselBanner.left_hand_banner ? 'right' : 'auto'"
                            desktop-image-class="w-full"
                        />
                    </template>
                </SlideCarousel>
            </div>

            <!-- Desktop / tablet: left 800×240 (or default) + right 400×240 (or default) -->
            <div
                v-if="showHomepageLayout"
                class="hidden sm:flex sm:flex-row sm:items-start sm:justify-center sm:gap-4"
            >
                <div
                    v-if="resolvedLeftHandBanners.length || showDefaultLeftSlot"
                    class="min-w-0 w-full max-w-[800px]"
                    :class="{ 'sm:flex-[2]': showRightHandSlot }"
                >
                    <SlideCarousel
                        v-if="resolvedLeftHandBanners.length"
                        :items="resolvedLeftHandBanners"
                        item-key="banner_id"
                        :slides-per-view="1"
                        :slides-per-view-md="1"
                        gap="0.75rem"
                        :show-arrows="showLeftCarouselArrows"
                        :show-dots="showLeftCarouselArrows"
                        arrow-placement="overlay"
                        :autoplay-delay="showLeftCarouselArrows ? 5000 : null"
                        :options="{ loop: showLeftCarouselArrows }"
                    >
                        <template #default="{ item: leftHandBanner }">
                            <BannerSingleImageTemplate
                                :banner="leftHandBanner"
                                :current-category-id="currentCategoryId"
                                :skip-product-modal="page === 'dashboard'"
                                gtm-creative-slot="homepage_left"
                                image-slot="left"
                                desktop-image-class="w-full aspect-[800/240] object-cover"
                            />
                        </template>
                    </SlideCarousel>

                    <a
                        v-else
                        href="/subscriptions"
                        class="dashboard-banner__default dashboard-banner__default--slot my-2 block"
                    >
                        <img
                            class="w-full aspect-[800/240] object-cover block rounded-md"
                            :src="defaultLeftBannerSrc"
                            :srcset="defaultLeftBannerSrcset"
                            alt="Subscriptions"
                            width="800"
                            height="240"
                        >
                    </a>
                </div>

                <div
                    v-if="showRightHandSlot"
                    class="min-w-0 w-full max-w-[400px] py-2"
                    :class="{ 'sm:flex-[1]': resolvedLeftHandBanners.length || showDefaultLeftSlot }"
                >
                    <BannerSingleImageTemplate
                        v-if="hasRightHandBanner"
                        :banner="rightHandBanner"
                        :current-category-id="currentCategoryId"
                        :skip-product-modal="page === 'dashboard'"
                        gtm-creative-slot="homepage_right"
                        image-slot="right"
                        desktop-image-class="w-full aspect-[400/240] object-cover"
                    />
                    <a
                        v-else
                        href="/shop/consumables"
                        class="dashboard-banner__default dashboard-banner__default--slot block"
                    >
                        <img
                            class="w-full aspect-[400/240] object-cover block rounded-md"
                            :src="defaultRightBannerSrc"
                            :srcset="defaultRightBannerSrcset"
                            alt="Shop consumables"
                            width="400"
                            height="240"
                        >
                    </a>
                </div>
            </div>
        </div>

        <!-- Category / core banner carousel -->
        <div
            v-if="page === 'category' || page === 'preview'"
            class="category-banner"
            :class="{ 'mt-8': Boolean(page === 'preview') }"
        >
            <SlideCarousel
                v-if="resolvedCategoryBanners.length"
                :items="resolvedCategoryBanners"
                item-key="banner_id"
                :slides-per-view="1"
                :slides-per-view-md="1"
                gap="0.75rem"
                :show-arrows="showCategoryCarouselArrows"
                :show-dots="showCategoryCarouselArrows"
                arrow-placement="overlay"
                :autoplay-delay="showCategoryCarouselArrows ? 5000 : null"
                :options="{ loop: showCategoryCarouselArrows }"
            >
                <template #default="{ item: categoryBanner }">
                    <BannerClickWrapper
                        :banner="categoryBanner"
                        :current-category-id="currentCategoryId"
                        :gtm-creative-slot="resolvedCategoryGtmCreativeSlot"
                    >
                        <img
                            class="w-full max-w-[592px] aspect-[592/296] object-cover rounded-md"
                            :src="categoryBanner.category_banner_mobile"
                            :alt="categoryBanner.category_banner_alt_text"
                            :data-banner-id="categoryBanner.banner_id"
                            width="592"
                            height="296"
                        >
                    </BannerClickWrapper>
                </template>
            </SlideCarousel>
        </div>
    </section>
</template>

<style scoped lang="scss">

.dashboard-banner {

    @apply relative max-w-7xl mx-auto sm:px-6 lg:px-8;

    &__default {

        &--slot {
            @apply relative;
        }
    }

}

.category-banner {
    @apply relative max-w-[592px] mx-auto;
}

</style>
